import React, { useEffect } from 'react';

import BgImage from '../../../assets/assetmark/bg-image.png';
import Logo from '../../../assets/assetmark/logo-white.png';

import { EditableField } from '../../../components/EditableField';

import { Typography } from '@mui/material';
import styles from './styles.module.css';
import { getSlideField } from 'pages/Conversations/components/SlidesEditor/utils';
import { Slide } from 'pages/Conversations/components/SlidesEditor/_data';
import { SlideTitle } from '../slide-title';
import { useGetCaseById } from 'pages/Cases/hooks/useGetCaseById';

export type CoverProps = [
  {
    field: 'title';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'subtitle1';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'case';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'caseName';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'owners1';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'owners2';
    label: string;
    value: string;
    type: 'text';
  },
  {
    field: 'date';
    label: string;
    value: string;
    type: 'text';
  },
];

export const Cover = React.memo(
  ({
    slide,
    onChangeSlide,
  }: {
    slide: Slide<CoverProps>;
    onChangeSlide: (slide: Slide<CoverProps>, content: CoverProps) => void;
  }): React.ReactElement => {
    const { caseData, isCaseLoading } = useGetCaseById({
      id: getSlideField(slide, 'case')?.value.caseId,
    });

    useEffect(() => {
      if (caseData && !isCaseLoading) {
        // Verificamos si los campos de owners ya existen
        const hasOwners = slide.content.some(
          field => field.field === 'owners1' || field.field === 'owners2',
        );

        if (!hasOwners) {
          const owners = Array.isArray(caseData?.owners)
            ? caseData.owners
                .map(owner => `${owner.firstName} ${owner.lastName}`)
                .filter(Boolean)
            : [];

          const newContent = [...slide.content];

          // Añadimos owners1 siempre
          newContent.push({
            field: 'owners1',
            label: 'Owners',
            value: owners[0].toString() || '',
            type: 'text',
          });

          // Añadimos owners2 si existe
          if (owners.length > 1) {
            newContent.push({
              field: 'owners2',
              label: 'Owners',
              value: owners[1].toString() || '',
              type: 'text',
            });
          }

          onChangeSlide(slide, newContent as CoverProps);
        }
      }
    }, [caseData, isCaseLoading, slide, onChangeSlide]);

    if (isCaseLoading) {
      return <div>Loading...</div>;
    }

    return (
      <div
        className={styles.cover}
        style={{ backgroundImage: `url(${BgImage})` }}
      >
        <div className={styles.wrapper}>
          <img className={styles.logo} src={Logo} alt="AMK Logo" />
          <div className={styles.title}>
            <SlideTitle style={{ color: '#FFF', fontSize: '70px' }}>
              {getSlideField(slide, 'title')?.value}
            </SlideTitle>
          </div>
          <div className={styles.preparedFor}>
            <Typography component="p" className={styles.preparedForTitle}>
              Prepared For
            </Typography>
            <EditableField
              field="caseName"
              slide={slide}
              onSlideContentChange={onChangeSlide}
              style={{
                fontFamily: 'Roboto',
                fontWeight: '500',
                fontSize: '50px',
                color: '#04ACD7',
              }}
            />
          </div>
          <div className={styles.preparedBy}>
            <Typography component="p" className={styles.preparedForTitle}>
              Prepared By
            </Typography>
            <EditableField
              field="owners1"
              slide={slide}
              onSlideContentChange={onChangeSlide}
              style={{
                fontFamily: 'Roboto',
                fontWeight: '400',
                fontSize: '30px',
                color: '#04ACD7',
              }}
            />
            <EditableField
              field="owners2"
              slide={slide}
              onSlideContentChange={onChangeSlide}
              style={{
                fontFamily: 'Roboto',
                fontWeight: '400',
                fontSize: '30px',
                color: '#04ACD7',
              }}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <div>For financial advisors only.</div>
          <div>
            {
              <div>
                © {new Date().getFullYear()} AssetMark, Inc. All rights
                reserved.
              </div>
            }
          </div>
        </div>
      </div>
    );
  },
);
